import React, { useEffect } from "react";
import { useLocation, Link, useNavigate } from "react-router-dom";
import { SEO } from "../components/SEO";
import { ChevronLeft, Play, CheckCircle2, ArrowRight } from "lucide-react";
import { Button } from "../components/ui/Button";

type PageData = {
  title: string;
  subtitle: string;
  badge: string;
  heroImage: string;
  videoUrl?: string;
  intro: string;
  features: { title: string; desc: string }[];
  details: string[];
};

const pagesData: Record<string, PageData> = {
  "/erp": {
    badge: "Módulo Principal",
    title: "ERP Completo",
    subtitle: "Gestão completa da sua oficina automotiva de ponta a ponta.",
    heroImage: "https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?auto=format&fit=crop&q=80&w=2000",
    videoUrl: "https://www.youtube-nocookie.com/embed/zpOULjyy-n8?rel=0",
    intro: "O software de gestão definitivo que centraliza compras, vendas, estoque, ordens de serviço e financeiro integrado na palma da sua mão.",
    features: [
      { title: "Plataforma Única", desc: "Abandone o papel e as planilhas soltas. Tudo conectado em tempo real." },
      { title: "Segurança na Nuvem", desc: "Seus dados armazenados em servidores da Amazon AWS com backup automático." },
      { title: "Múltiplas Filiais", desc: "Gerencie todas as lojas com consolidação central de metas e fluxo." }
    ],
    details: [
      "Integração contábil com SPED e exportação automática de notas XMLs e extratos.",
      "Treinamentos ao vivo incluídos na implantação para dominar todo o ambiente."
    ]
  },
  "/crm": {
    badge: "Vendas",
    title: "CRM Automotivo",
    subtitle: "Fidelize a carteira de clientes com avisos automáticos e gatilhos.",
    heroImage: "https://images.unsplash.com/photo-1552664730-d307ca884978?auto=format&fit=crop&q=80&w=2000",
    intro: "Vender na primeira vez é bom. Fazer o cliente voltar três vezes ao ano é incrível. O CRM automotivo atua proativamente mantendo seu cliente por perto.",
    features: [
      { title: "Avisos de Revisão", desc: "Gera alertas apontando que fazem 6 meses ou 10 mil KM desde a última O.S." },
      { title: "WhatsApp Conectado", desc: "Botões de atalho da ficha geram textos saudações customizadas no WhatsApp web." },
      { title: "Métricas de Retenção", desc: "Saiba quantos clientes sumiram na curva e elabore ofertas ativas de reativação." }
    ],
    details: [
      "Listas filtráveis de clientes VIP ou Curva ABC de maiores faturamentos do ano.",
      "Lembretes de parabéns no aniversário do cliente ou da frota empresarial credenciada."
    ]
  },
  "/agendamento": {
    badge: "Módulo Operacional",
    title: "Agendamento Online",
    subtitle: "Chega de fila e espera infinita para recepcionar carros na porta.",
    heroImage: "https://images.unsplash.com/photo-1506784365847-bbad939e9335?auto=format&fit=crop&q=80&w=2000",
    intro: "Sua agenda digital sincronizada conectando elevações do pátio, funcionários alocados e os compromissos dos vistoriadores na palma do App.",
    features: [
      { title: "Horários Sincronizados", desc: "Agenda visual dinâmica interligada a disponibilidade de cada elevador." },
      { title: "Lembretes Automáticos", desc: "Os clientes são lembrados pelo WhatsApp no final de semana da revisão." },
      { title: "Evite Gafes", desc: "Checagem de placa prévia já levanta eventuais serviços que ficaram pendentes no último balanço." }
    ],
    details: [
      "Permite adicionar links públicos para os clientes solicitarem horas sozinho (Self Service).",
      "Confirmações visuais e Kanban dos veículos dia-a-dia."
    ]
  },
  "/fiscal": {
    badge: "Emissão e Tributário",
    title: "Módulo Fiscal",
    subtitle: "Emissor blindado e focado na complexidade tributária veicular.",
    heroImage: "https://images.unsplash.com/photo-1450101499163-c8848c66ca85?auto=format&fit=crop&q=80&w=2000",
    intro: "ICMS, CST, NCM, Substituição Tributária (ST) e impostos estaduais param de doer a cabeça. Um emissor pensado pra resolver em poucos cliques e transmitir em segundos.",
    features: [
      { title: "Homologado Nacional Padrões SEFAZ", desc: "Funciona tanto com emissão massiva NF-e (A4) quanto na ponta em impressoras térmicas pelo NFC-e ou SAT-SP" },
      { title: "Recálculos Dinâmicos", desc: "Evita perdas quando clientes são empresariais ou frota em cenários de partilha ICMS interestaduais sem falhar." },
      { title: "Envio ao Contador", desc: "Balanço limpo enviado compactado automaticamente aos 5 de todo o mês via e-mail corporativo." }
    ],
    details: [
      "Cadastro inteligente com buscas ativas NCM nas bases atualizadas mercadológicas auto peças.",
      "Configurações Tributárias fixas pré definidas com a sua contabilidade pra zerar digitações diárias cegas."
    ]
  },
  // Operacional
  "/catalogo-vendas": {
    badge: "Módulo Operacional",
    title: "Catálogo de Vendas",
    subtitle: "Gerencie pedidos e notas de forma eficiente e rápida.",
    heroImage: "https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?auto=format&fit=crop&q=80&w=2000",
    videoUrl: "https://www.youtube-nocookie.com/embed/zpOULjyy-n8?rel=0",
    intro: "Transforme a maneira como você vende e expõe os produtos da sua oficina e autopeças. O módulo Catálogo de Vendas consolida informações em tempo real e emite notas com facilidade.",
    features: [
      { title: "Gestão Unificada", desc: "Controle todo o ciclo de vendas a partir de um único painel intuitivo." },
      { title: "Emissão Rápida de Notas", desc: "Emita NF-e e NFC-e em poucos cliques, sem retrabalho na digitação." },
      { title: "Controle de Metas", desc: "Avalie o desempenho do seu time de vendas instantaneamente." },
    ],
    details: [
      "Acompanhe históricos de compras dos clientes para oportunidades de remarketing.",
      "Criação e aprovação de orçamentos pelo WhatsApp, facilitando a interação online.",
      "Visualize com a mesma facilidade em dispositivos desktop e móveis."
    ]
  },
  "/pdv": {
    badge: "Módulo Operacional",
    title: "PDV Frente Caixa",
    subtitle: "Vendas rápidas com NFC-e diretamente no balcão.",
    heroImage: "https://images.unsplash.com/photo-1556740758-90de374c12ad?auto=format&fit=crop&q=80&w=2000",
    videoUrl: "https://www.youtube-nocookie.com/embed/zpOULjyy-n8?rel=0",
    intro: "O Ponto de Venda do RosTech foi desenhado para quem não pode perder um segundo sequer. Feche vendas, informe CPF na nota e controle fluxo de caixa em uma interface a jato.",
    features: [
      { title: "Velocidade na Fila", desc: "Otimizado para leitores de código de barras e teclas de atalho rápido." },
      { title: "Integração Pura", desc: "Uma vez finalizada a venda, o estoque e financeiro já são atualizados." },
      { title: "Compatível com Maquininha", desc: "Integrado com TEF para automatizar os seus recebimentos em cartão." },
    ],
    details: [
      "Fechamento e abertura de caixa simplificados com extrato em tempo real.",
      "Suporte a múltiplos meios de pagamento mistos na mesma venda.",
      "Contingência offline: a internet caiu? O PDV continua emitindo NFC-e."
    ]
  },
  "/ordem-servico": {
    badge: "Módulo Operacional",
    title: "Ordem de Serviço",
    subtitle: "Controle de ponta a ponta da entrada do veículo até o faturamento.",
    heroImage: "https://images.unsplash.com/photo-1632823469806-69f83f2a58ce?auto=format&fit=crop&q=80&w=2000",
    videoUrl: "https://www.youtube-nocookie.com/embed/zpOULjyy-n8?rel=0",
    intro: "O coração de qualquer oficina mecânica. Nossa OS permite anexar fotos do veículo, checklists digitais e controlar horas produtivas com precisão cirúrgica.",
    features: [
      { title: "Checklist Digital", desc: "Envie avaliações ilustradas por WhatsApp para aprovação imediata." },
      { title: "Controle de Pára-choque a Pára-choque", desc: "Tenha registros blindados do que foi feito, onde, quando e por qual mecânico." },
      { title: "Alocação de Tempo", desc: "Veja as horas produtivas do time para faturar corretamente as horas mecânicas." },
    ],
    details: [
      "Integração imediata com orçamentos pré-aprovados e requisição de peças.",
      "Painel Kanban visual para ver todos os carros no pátio e seus status.",
      "Notificação automática via SMS ou WhatsApp para o cliente quando pronto."
    ]
  },
  "/estoque": {
    badge: "Módulo Operacional",
    title: "Controle de Estoque",
    subtitle: "Controle produtos, peças e avise baixas dinamicamente.",
    heroImage: "https://images.unsplash.com/photo-1586528116311-ad8ed7c152dcc?auto=format&fit=crop&q=80&w=2000",
    videoUrl: "https://www.youtube-nocookie.com/embed/zpOULjyy-n8?rel=0",
    intro: "Pare de comprar errado. O RosTech controla estoque por marca, localização na prateleira, compatibilidade e rastreia peças sumidas com requisição atrelada à O.S.",
    features: [
      { title: "Aviso de Estoque Mínimo", desc: "Receba alertas gerados automaticamente para repor produtos de curva A." },
      { title: "Inventário Cíclico", desc: "Auditorias feitas por setor para nunca precisar fechar a loja inteira." },
      { title: "Curva ABC", desc: "Aponte o capital investido corretamente nas peças de giro e evite estoques ociosos." },
    ],
    details: [
      "Controle total de produtos substitutos e peças similares no momento da venda.",
      "Monitoramento de histórico de compras para negociações com fornecedores.",
      "Kits dinâmicos, para vender um 'Kit Revisão 10.000km' e dar baixa em peças variadas."
    ]
  },
  "/venda-cruzada": {
    badge: "Módulo Operacional",
    title: "Venda Cruzada",
    subtitle: "Aumente o ticket médio por OS com recomendação inteligente.",
    heroImage: "https://images.unsplash.com/photo-1552581234-26160f608093?auto=format&fit=crop&q=80&w=2000",
    intro: "Não limite seus rendimentos. O módulo de venda cruzada usa inteligência para sugerir itens correlacionados quando uma ordem de serviço está sendo montada.",
    features: [
      { title: "Sugestões Preditivas", desc: "Exemplo: ao orçar troca de óleo, o sistema sugere o filtro de óleo." },
      { title: "Aumentando o Faturamento", desc: "Pequenos acréscimos constantes resultam em dezenas de milhares a mais no fim do mês." },
      { title: "Configurações Flexíveis", desc: "Crie suas próprias lógicas de correlação de itens e serviços." },
    ],
    details: [
      "Integração instantânea em O.S e Vendas de Balcão (PDV).",
      "Relatórios que mostram o ganho extra proporcionado pela ferramenta.",
      "Pode ser usado também com vendas de perfumaria e lavagem na recepção do carro."
    ]
  },

  // Financeira
  "/contas-pagar": {
    badge: "Gestão Financeira",
    title: "Contas a Pagar",
    subtitle: "Controle de despesas e evitar retrabalho com integração.",
    heroImage: "https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?auto=format&fit=crop&q=80&w=2000",
    videoUrl: "https://www.youtube-nocookie.com/embed/zpOULjyy-n8?rel=0",
    intro: "Mapeie todo seu centro de custos. Fornecedores de peças, funcionários, luz, aluguel: a nossa gestão de contas a pagar elimina perdas e garante conformidade.",
    features: [
      { title: "Projeção de Pagamentos", desc: "Visualize o fluxo futuro garantindo que você nunca fique inadimplente." },
      { title: "Centro de Custos", desc: "Saiba exatamente quanto custa o pátio vs. escritório." },
      { title: "Anexos e Documentos", desc: "Guarde os comprovantes PDF direto no sistema." },
    ],
    details: [
      "Integração do XML de entrada direto no Contas a Pagar.",
      "Categorizações dinâmicas e DRE automática."
    ]
  },
  "/contas-receber": {
    badge: "Gestão Financeira",
    title: "Contas a Receber",
    subtitle: "Gestão de recebimentos e eliminação da inadimplência.",
    heroImage: "https://images.unsplash.com/photo-1579621970563-ebec7560ff3e?auto=format&fit=crop&q=80&w=2000",
    intro: "Ter lucro no papel e o caixa vazio é o pior pesadelo. Monitore ativamente todos os cheques pré-datados, notas, carnês e cartões de crédito em trânsito.",
    features: [
      { title: "Monitoramento Ativo", desc: "Receba alertas sobre boletos atrasados instantaneamente." },
      { title: "Emissão de Boletos", desc: "Geração por dentro do RosTech sem arquivo remessa (PIX e Boleto híbrido)." },
      { title: "Cobrança Automatizada", desc: "Régua inteligente via WhatsApp disparando avisos aos devedores." },
    ],
    details: [
      "Integração transparente com parcelamento do cartão de crédito.",
      "Visualização das faturas vinculadas à OS ou venda balcão do cliente."
    ]
  },
  "/controle-caixa": {
    badge: "Gestão Financeira",
    title: "Controle de Caixa",
    subtitle: "Fluxo e conciliação bancária de forma segura.",
    heroImage: "https://images.unsplash.com/photo-1554224155-8d04cb21cd6c?auto=format&fit=crop&q=80&w=2000",
    intro: "Audite o caixa diário facilmente e implemente a conciliação bancária importando os extratos (OFX). Não deixe passar nenhum centavo não computado.",
    features: [
      { title: "Abertura e Fechamento", desc: "Processo rigoroso para sangria e suprimento garantindo responsabilidade dos caixas." },
      { title: "Conciliação OFX", desc: "Exporte seu extrato bancário e faça a conciliação em segundos." },
      { title: "Fluxo Multicaixa", desc: "Para oficinas com múltiplos PDVs ou mais de um atendente operando valores." },
    ],
    details: [
      "Evite diferenças com conciliação automática de recebimentos POS.",
      "Histórico guardado na nuvem imutável para auditorias posteriores."
    ]
  },
  "/relatorios": {
    badge: "Gestão Financeira",
    title: "Insights & Relatórios",
    subtitle: "Análises de performance, DRE e curva ABC.",
    heroImage: "https://images.unsplash.com/photo-1460925895917-afdab827c52f?auto=format&fit=crop&q=80&w=2000",
    intro: "Tome decisões baseadas em dados, não em intuição. Relatórios visuais avançados apontam se a sua oficina está de fato dando lucro ou apenas trocando dinheiro.",
    features: [
      { title: "DRE Gerencial", desc: "O demonstrativo de resultados do exercício adaptado à realidade da oficina." },
      { title: "Gestão de Horas", desc: "Relatório de produtividade mecânica vs tempos cobrados." },
      { title: "Dashboards em Tempo Real", desc: "Metas de vendas mensais rodando em telas interativas." }
    ],
    details: [
      "Analistas Financeiro (Socio Terceiro)",
      "Orientação e cortes precisos mensais para reerguer fluxos baixos."
    ]
  },
  // Indústrias
  "/industrias/oficina-mecanica": {
    badge: "Indústria",
    title: "Oficina Mecânica",
    subtitle: "O ERP que entende a graxa e as metas. Gestão completa.",
    heroImage: "https://images.unsplash.com/photo-1504222490345-c075b6008014?auto=format&fit=crop&q=80&w=2000",
    videoUrl: "https://www.youtube-nocookie.com/embed/zpOULjyy-n8?rel=0",
    intro: "RosTech é focado nas reais dores das oficinas automotivas. Do orçamento online com checklist fotográfico até ao fechamento do caixa do final de expediente.",
    features: [
      { title: "Cadastro de Veículos", desc: "Placa, chassi, motor, e histórico do veículo vitalício atrelado ao cliente." },
      { title: "Requisição de Peças", desc: "Controle em tempo real de retirada do estoque sem furar o físico com o sistema." },
      { title: "Checklist Digital", desc: "Dê adeus ao papelão. Use tablets via web para apontar as melhorias no carro." },
    ],
    details: [
      "Controle de produtividade do pátio com horas do mecânico registradas.",
      "O.S. modular que permite adicionar e faturar avulso."
    ]
  },
  "/industrias/autopecas": {
    badge: "Indústria",
    title: "Moto e Autopeças",
    subtitle: "Emissão fiscal, giro de estoque veloz e controle.",
    heroImage: "https://images.unsplash.com/photo-1619642751034-765dfdf7c58e?auto=format&fit=crop&q=80&w=2000",
    videoUrl: "https://www.youtube-nocookie.com/embed/zpOULjyy-n8?rel=0",
    intro: "O balcão não pode parar. Seu caixa precisa de velocidade, sua nota de fluidez, e seu estoque de alertas inteligentes de compra.",
    features: [
      { title: "PDV Imediato", desc: "Venda rápida com leitor de código de barras e devolução descomplicada." },
      { title: "Emissão NFe e NFCe", desc: "XML armazenado na nuvem. Envio para contador automático no começo do mês." },
      { title: "Tabela de Compatibilidade", desc: "Para buscar facilmente 'Pastilha do Palio 2012 serve no Uno 2014?'." },
    ],
    details: [
      "Integração contábil avançada com contadores para apuração de impostos.",
      "Gerenciador Múltiplo de Fornecedores e curvas ABC."
    ]
  },
  "/industrias/centro-automotivo": {
    badge: "Indústria",
    title: "Centro Automotivo",
    subtitle: "A combinação perfeita entre loja física e ordem de serviço.",
    heroImage: "https://images.unsplash.com/photo-1599256241322-c3614275eac1?auto=format&fit=crop&q=80&w=2000",
    videoUrl: "https://www.youtube-nocookie.com/embed/zpOULjyy-n8?rel=0",
    intro: "Vender pneu e depois instalar. O centro automotivo exige balcão rápido e ao mesmo tempo controle do pátio para não encavalar clientes nos elevadores.",
    features: [
      { title: "Kit Revisão Automático", desc: "Crie pacotes (Pneu + Alinhamento + Balanceamento) de forma unificada no sistema." },
      { title: "Painel de Montagem", desc: "Agenda visual para saber qual elevador está ocupado e por quanto tempo." },
      { title: "Venda em 2 Fases", desc: "Orçado no balcão e executado no pátio, com sincronia total e comissões divididas." },
    ],
    details: [
      "Relatório comparando as margens entre serviços (mão-de-obra) vs produtos (pneus).",
      "Controle de Garantias atrelado à NF para blindar jurídicamente."
    ]
  },
  "/industrias/funilaria": {
    badge: "Indústria",
    title: "Funilaria e Pintura",
    subtitle: "Controle de metas seguradoras e custos com materiais e tintas.",
    heroImage: "https://images.unsplash.com/photo-1542385150-13a8d11634b3?auto=format&fit=crop&q=80&w=2000",
    videoUrl: "https://www.youtube-nocookie.com/embed/zpOULjyy-n8?rel=0",
    intro: "Cálculo real do uso de tintas, verniz, fitas. A Funilaria tem um gasto muito diluído. Mostramos como faturar mais com frotas e seguradoras.",
    features: [
      { title: "Integração a Terceiros", desc: "Controle de franqueados e faturamento unificado para grandes seguradoras." },
      { title: "Controle de Materiais de Consumo", desc: "Rateio fácil do custo de vernizes entre as O.S." },
      { title: "Fotos da Evolução", desc: "Mostre o 'antes, durante e depois' enviando atualizações ao WhatsApp do cliente." },
    ],
    details: [
      "O.S. mais flexíveis para adicionar horas não programadas de repuxo de massa.",
      "Aviso de finalização rápido para desafogar pátios com avarias menores."
    ]
  },
  "/industrias/lava-jato": {
    badge: "Indústria",
    title: "Lava Jato e Estética",
    subtitle: "Rastreio e cadastro ágil. Controle diário do ticket e movimento.",
    heroImage: "https://images.unsplash.com/photo-1520340356584-f9917d1eea6f?auto=format&fit=crop&q=80&w=2000",
    videoUrl: "https://www.youtube-nocookie.com/embed/zpOULjyy-n8?rel=0",
    intro: "Não exige longos orçamentos. Simplesmente o cliente chega e precisa de uma Wash OS ou Pacote de Estética avançado com vitrificação.",
    features: [
      { title: "Check-in Relâmpago", desc: "Aponte só a placa, nome o sistema já auto-preenche se for recorrente." },
      { title: "Comissões dos Lavadores", desc: "Esqueça a briga de caderno para saber quem lavou e quanto leva." },
      { title: "CRM de Retorno", desc: "Lembre o cliente automaticamente que já faz 30 dias que ele não aparece." },
    ],
    details: [
       "Controle de Lavagem a seco, higienização de ar, oxi-sanitização.",
       "Controle de fiado, pendura e convênios empresariais simplificado."
    ]
  },
  "/industrias/auto-glass": {
    badge: "Indústria",
    title: "Auto Glass e Acessórios",
    subtitle: "Especialista em reparo de vidros e instalação de películas e som.",
    heroImage: "https://images.unsplash.com/photo-1601362840469-51e4d8d58785?auto=format&fit=crop&q=80&w=2000",
    videoUrl: "https://www.youtube-nocookie.com/embed/zpOULjyy-n8?rel=0",
    intro: "Orçamentos visuais. Facilidade de acoplar fotos de pára-brisas trincados no sistema e gerar orçamentos rápidos para convênios e usuários comuns.",
    features: [
      { title: "Venda Híbrida", desc: "Lide facilmente com película automotiva cobrada por rolo, tamanho de área e mão de obra em minutos." },
      { title: "Garantias Visuais", desc: "Termos de responsabilidade vinculados ao chassi." },
      { title: "Convênios", desc: "Fácil gestão de pagamentos por apólices com fluxo central no sistema." },
    ],
    details: [
       "Controle flexível de itens complementares e miudezas.",
       "Permite emitir termo de liberação online com assinatura ou foto."
    ]
  },

  // Soluções
  "/solucoes/vende-online": {
    badge: "Solução",
    title: "Quem Vende Online",
    subtitle: "Gerencie processos de vendas multicanal sem estresse.",
    heroImage: "https://images.unsplash.com/photo-1556740714-a8395b3bf30f?auto=format&fit=crop&q=80&w=2000",
    intro: "Pessoas compram no balcão ou compram pela internet (Shopee/MercadoLivre). Seu estoque não pode perder o rumo entre os dois canais.",
    features: [
      { title: "Gerenciador Multicanal", desc: "Unifica o saldo de todas as pontas em uma única ficha estendida de painel." },
      { title: "Nota Fiscal em Massa", desc: "Fature e emita centenas da NFe com importação automática da plataforma de vendas." },
      { title: "Logística Ágil", desc: "Separação por 'Picking list' da expedição automotiva." }
    ],
    details: [
      "Integrações robustas simplificam operação e poupam o quadro de funcionários.",
      "Redução de multas de marketplaces devido ao baixo índice de quebras de estoque."
    ]
  },
  "/solucoes/loja-fisica": {
    badge: "Solução",
    title: "Tem Loja Física",
    subtitle: "Frente de caixa, controle fiscal apurado e multi-lojas.",
    heroImage: "https://images.unsplash.com/photo-1604719312566-8912e9227c6a?auto=format&fit=crop&q=80&w=2000",
    intro: "O cliente fisicamente presente na loja cobra velocidade e atendimento humanizado.",
    features: [
      { title: "Emissor Fiscal Ultra Rápido", desc: "Não irrite a fila com sistemas travados. Módulo offline de NFC-e habilitável." },
      { title: "Multi-estoque Inteligente", desc: "O Vendedor descobre logo na busca se a peça acabou na filial atual e em qual outra filial tem saldo." },
      { title: "Meta de Vendedores", desc: "Gamifique seu caixa para impulsionar a performance dos balconistas." }
    ],
    details: [
      "Compatível com emissão de SAT (SP) MFe e outras regulações locais.",
      "Troca rápida gerando crédito rotativo para o cliente abater no ato (Vale Troca)."
    ]
  },
  "/solucoes/oferece-servico": {
    badge: "Solução",
    title: "Prestador de Serviço",
    subtitle: "Sistemas para Oficinas que vivem da sua mão de obra qualificada.",
    heroImage: "https://images.unsplash.com/photo-1599256241322-c3614275eac1?auto=format&fit=crop&q=80&w=2000",
    intro: "Ordem de serviço que respeita processos ágeis e links de pagamento eficientes. Sua hora de mão de obra precisa ser valorizada sem desperdício.",
    features: [
      { title: "Cronômetro do Mecânico", desc: "Mensure horas estimadas vs gastas, apurando de fato o lucro em serviços técnicos." },
      { title: "Aprovação por WhatsApp", desc: "Elimine atritos e burocracia do cliente que só quer um link e um aceite com ok." },
      { title: "Aviso Via SMS/Whatsapp", desc: "Chegou no estágio faturado? o cliente recebe automaticamente um alerta validando." }
    ],
    details: [
      "Envie boleto ou link pague-fácil evitando fiados de porta.",
      "Atendimento estético rápido sem o inchaço de processos fabris de empresas gigantes."
    ]
  },
  "/solucoes/pequena-industria": {
    badge: "Solução",
    title: "Pequena Indústria Mecânica",
    subtitle: "Ordens de Produção alinhadas ao backoffice contábil.",
    heroImage: "https://images.unsplash.com/photo-1581091226825-a6a2a5aee158?auto=format&fit=crop&q=80&w=2000",
    intro: "Usinagem de peças, fresadoras, controle de tornos e retíficas exigem apontamentos estruturados que misturam consumíveis brutos com o item resultante finalizado.",
    features: [
      { title: "Ficha Técnica do Produto", desc: "Liste quantos quilos de aço etc o item retificado gasta numa arruela finalizada." },
      { title: "Baixa em Insumo/Matéria", desc: "Ao criar finalizado a quantidade de sub-peças some em cascata automática." },
      { title: "Ordem de Produção O.P.", desc: "Gestão detalhada com cronogramas diários previstos industriais em pequena escala." }
    ],
    details: [
       "Suporta importações avançadas contábeis pra rastreio CST.",
       "Adequado para fábricas de baterias recondicionadas, tornos CNC parceiros do agro ou do automotivo."
    ]
  },

  // Recursos
  "/recursos/envios": {
    badge: "Recurso Exclusivo",
    title: "Envios Integrados",
    subtitle: "Gerencie logística em um clique interligado com múltiplas transportadoras.",
    heroImage: "https://plus.unsplash.com/premium_photo-1661963473136-1218151ced9f?auto=format&fit=crop&q=80&w=2000",
    intro: "Gire autopeças a todo o vapor integrando envio fácil aos Correios, Jadlog, Loggi e muito mais.",
    features: [
      { title: "Etiquetas Multi-Volume", desc: "Gerar etiquetas já baseadas na volumetria dos pneus e pacotes cadastrados da sua loja." },
      { title: "Código de Rastreio", desc: "Ao colar a etiqueta na caixa e disparar baixa o cliente recebe notificação de rota rastreio online." },
      { title: "Cotação Dinâmica Cesto", desc: "Reduza custos com taxas oferecendo na ponta cotação viável barata ao mercado." }
    ],
    details: [
        "Apoiado em redes robustas logísticas como Kangu ou Melhor Envio e Correios Sigep web."
    ]
  },
  "/recursos/conta-digital": {
    badge: "Recurso Exclusivo",
    title: "RosTech Conta Digital PJ",
    subtitle: "Integração automática na balança diária de extrato com o seu banco e TEF nativo.",
    heroImage: "https://images.unsplash.com/photo-1563013544-824ae1b704d3?auto=format&fit=crop&q=80&w=2000",
    intro: "Pague o pró-labore ou seus fornecedores e quite guias na nuvem através do ambiente bancário amigável vinculado aos lucros diários que entra no balcão da RosTech.",
    features: [
      { title: "Pix de Baixa Direto em Fatura", desc: "Uma vez que for pago o Qrcode PIX do RosTech o sistema abaixa sua nota, não precise ver comprovantes." },
      { title: "Emissão Zero Tarifa", desc: "Receba carnês das mensalidades corporativas pra PJ local e funilarias na régua RosTech bank e elimine as taxas exorbitantes tradicionais." },
      { title: "Dinheiro a Vista", desc: "Suas vendas já estão liquidadas sem fricções pra fluir seu estoque no dia seguinte." }
    ],
    details: [
       "100% regulado bacen protegido fundo FGC da provedora matriz do Bank.",
       "Emissão de cartão pré-pago físico p/ gerir a comprar fura de caixas ou diárias oficinas."
    ]
  },
  "/recursos/maquininha": {
    badge: "Recurso Exclusivo",
    title: "Maquininha Smart TEF POS",
    subtitle: "Automação no pagamento: a maquininha envia e processa na ponta direto dentro da OS do sistema.",
    heroImage: "https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?auto=format&fit=crop&q=80&w=2000",
    intro: "Sem perdas na digitação de R$10,00 que vira R$1,00 acidentalmente na ponta, a Smart TEF POS atrela e cruza automaticamente no seu financeiro do dia a transação da administradora do cartão de débito/crédito.",
    features: [
      { title: "Sem Fio / Wi-fi Integrado", desc: "Leva o terminal em frente da catraca ou baía pro seu consultor receber longe da bancada." },
      { title: "Relatório Consolidado POS", desc: "Sem perder tardes conferindo fitinhas, consolidador mostra 100% repassado contra retido MDR." },
      { title: "Divisão Splits", desc: "Opere cartões dividindo automaticamente repasses terceirizados (o da loja e o da vitrificadora / eletricista)." }
    ],
    details: [
      "Taxas exclusivas agressivas formatadas focado na alta volumetria das pequenas redes automotivas."
    ]
  },
  "/recursos/mentoria": {
    badge: "Recurso Exclusivo",
    title: "RosTech Mentorias de Elite",
    subtitle: "Acompanhamento profissional voltado ao proprietário focado em escala das lideranças.",
    heroImage: "https://images.unsplash.com/photo-1517245386807-bb43f82c33c4?auto=format&fit=crop&q=80&w=2000",
    videoUrl: "https://www.youtube-nocookie.com/embed/zpOULjyy-n8?rel=0",
    intro: "Ter sistema foda não serve se o lider local perdeu as noções das prioridades operativas do processo. Participe online do portal ensinando marketing e lucro com instrutores da RosTech Academy.",
    features: [
      { title: "Encontros de Masterminds e Imersões Ocultas", desc: "Reuniões entre donos oficina debatendo saídas mercadológicas escaláveis, desafios salariais em polos Brasil adentro e afora." },
      { title: "Biblioteca On demand Premium", desc: "Video-aulas base com treinamentos gerenciais com focos restritivos no alto ganho lucro sem estresse da concorrência de esquadrão esquina." },
      { title: "Analistas Financeiro (Socio Terceiro)", desc: "Uma dupla do suporte RosTech cruza números anuais com consultor da sua loja, e orienta cortes precisos mensais para reerguer fluxos baixos." }
    ],
    details: [
      "Não inclusa na modalidade basic, destrave os Módulos pro pra usufruir da aceleração."
    ]
  },
  "/modulos": {
    badge: "Visão Geral",
    title: "Todos os Módulos",
    subtitle: "Conheça o ecossistema completo do RosTech para a sua empresa.",
    heroImage: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?auto=format&fit=crop&q=80&w=2000",
    intro: "Tudo o que você precisa em uma única plataforma. Elimine o retrabalho de usar sistemas paralelos, planilhas avulsas e cadernos.",
    features: [
      { title: "Gestão Operacional", desc: "PDV, Estoque, Ordem de Serviço, Venda Cruzada e Catálogos." },
      { title: "Gestão Financeira", desc: "Contas a Pagar/Receber, Controle de Caixa e Relatórios (DRE)." },
      { title: "Recursos Opcionais", desc: "Envios Integrados, Conta Digital PJ e Maquininha Integrada." }
    ],
    details: [
      "Integração perfeita onde o dado inserido no pátio reflete instantaneamente no financeiro.",
      "Segurança na nuvem para não perder nenhuma informação vital."
    ]
  }
};

export const DynamicFeaturePage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const path = location.pathname;

  const data = pagesData[path];

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [path]);

  // Se não achar o path mapeado, poderia jogar na home ou 404
  if (!data) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] text-center px-4">
        <h1 className="text-2xl font-bold text-slate-800 mb-4">Página não encontrada ou em desenvolvimento</h1>
        <Button onClick={() => navigate("/")} variant="outline">Voltar ao site</Button>
      </div>
    );
  }

  return (
    <div className="bg-white min-h-screen">
      <SEO 
        title={`${data.title} | RosTech ERP`}
        description={data.subtitle}
        canonicalUrl={path}
      />

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 lg:pt-40 lg:pb-32 overflow-hidden">
        {/* Background Decorative */}
        <div className="absolute inset-0 z-0 bg-slate-900">
           <img 
              src={data.heroImage} 
              alt={data.title} 
              className="w-full h-full object-cover opacity-20"
           />
           <div className="absolute top-0 right-0 w-2/3 h-full bg-gradient-to-l from-transparent to-slate-900 pointer-events-none" />
           <div className="absolute bottom-0 left-0 w-full h-1/2 bg-gradient-to-t from-slate-900 to-transparent pointer-events-none" />
        </div>

        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <div className="max-w-3xl">
            <div className="inline-block px-3 py-1 bg-blue-600/20 text-blue-300 font-bold text-xs uppercase tracking-wider rounded-sm mb-6 border border-blue-500/30">
              {data.badge}
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-7xl font-extrabold text-white leading-tight mb-6 tracking-tight">
              {data.title}
            </h1>
            <p className="text-xl md:text-2xl text-slate-300 font-medium leading-relaxed mb-10 max-w-2xl">
              {data.subtitle}
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
               <Link to="/cadastro">
                 <Button size="lg" className="bg-blue-600 hover:bg-blue-700 text-white border-0 text-lg px-8 py-6 w-full sm:w-auto shadow-xl shadow-blue-900/50">
                    Testar Gratuitamente grátis
                 </Button>
               </Link>
               <a href="https://wa.me/5524992818632" target="_blank" rel="noopener noreferrer">
                 <Button size="lg" variant="outline" className="text-white border-slate-600 hover:bg-slate-800 text-lg px-8 py-6 w-full sm:w-auto">
                    Falar com Especialista
                 </Button>
               </a>
            </div>
          </div>
        </div>
      </section>

      {/* Intro & Feature Highlights */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
             <div>
                <h2 className="text-3xl md:text-4xl font-extrabold text-slate-900 mb-6">Tudo sob controle. Sem margem para erros.</h2>
                <p className="text-lg text-slate-600 leading-relaxed font-medium mb-10">
                  {data.intro}
                </p>

                <div className="space-y-8">
                  {data.features.map((feat, idx) => (
                    <div key={idx} className="flex items-start gap-4">
                       <div className="w-12 h-12 rounded-xl bg-blue-50 flex items-center justify-center shrink-0 border border-blue-100 mt-1">
                          <CheckCircle2 className="w-6 h-6 text-blue-600" />
                       </div>
                       <div>
                          <h3 className="text-xl font-bold text-slate-900 mb-2">{feat.title}</h3>
                          <p className="text-slate-600 leading-relaxed">{feat.desc}</p>
                       </div>
                    </div>
                  ))}
                </div>
             </div>

             {/* Right side Images/Video */}
             <div className="relative">
                {data.videoUrl ? (
                   <div className="w-full aspect-video rounded-3xl overflow-hidden shadow-2xl border-4 border-white bg-slate-900 transform lg:rotate-2">
                     <iframe 
                       width="100%" 
                       height="100%" 
                       src={data.videoUrl} 
                       title={`Demonstração ${data.title}`} 
                       allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" 
                       allowFullScreen
                       className="border-0"
                     ></iframe>
                   </div>
                ) : (
                   <div className="w-full aspect-[4/3] rounded-3xl overflow-hidden shadow-2xl border-4 border-white bg-slate-900 transform lg:-rotate-2 group">
                      <img 
                        src={data.heroImage} 
                        alt={data.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                      />
                   </div>
                )}
                
                {/* Floating detail box */}
                <div className="absolute -bottom-10 -left-10 bg-white p-6 rounded-2xl shadow-xl border border-slate-100 hidden md:block max-w-[280px]">
                   <h4 className="font-bold text-slate-900 mb-2">Visão Geral</h4>
                   <p className="text-sm text-slate-500 leading-relaxed">
                     Reduza o tempo de operações manuais. Nossos módulos convergem em estabilidade e métricas transparentes.
                   </p>
                </div>
             </div>
          </div>
        </div>
      </section>

      {/* Details & Advantages */}
      <section className="py-24 bg-slate-50 border-t border-slate-100">
        <div className="max-w-7xl mx-auto px-6">
           <div className="text-center max-w-3xl mx-auto mb-16">
              <h2 className="text-3xl md:text-4xl font-extrabold text-slate-900 mb-6">Por que escolher o RosTech?</h2>
              <p className="text-lg text-slate-600 font-medium">As vantagens de operar com o {data.title} desenhado por quem entende o seu mercado.</p>
           </div>
           
           <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {data.details.map((detail, idx) => (
                 <div key={idx} className="bg-white p-8 rounded-2xl shadow-sm border border-slate-100 hover:shadow-md transition-shadow">
                    <div className="w-10 h-10 rounded-full bg-slate-50 flex items-center justify-center mb-6 text-blue-600 font-bold border border-slate-100">
                       {idx + 1}
                    </div>
                    <p className="text-slate-700 font-medium leading-relaxed">{detail}</p>
                 </div>
              ))}
           </div>
        </div>
      </section>

      {/* CTA Final */}
      <section className="py-24 bg-blue-700 relative overflow-hidden">
        {/* Background elements */}
        <div className="absolute top-0 right-0 w-96 h-96 bg-blue-600 rounded-full blur-[100px] opacity-60"></div>
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-blue-800 rounded-full blur-[100px] opacity-60"></div>
        
        <div className="max-w-5xl mx-auto px-6 text-center relative z-10">
          <h2 className="text-4xl md:text-5xl font-extrabold text-white mb-6 leading-tight">
            Chega de usar sistemas pela metade.
          </h2>
          <p className="text-xl text-blue-100 mb-10 max-w-3xl mx-auto font-medium">
            Integre o <strong className="text-white">{data.title}</strong> com todo o seu ecossistema. Foque no que importa: crescimento e rentabilidade do seu negócio.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link to="/cadastro">
               <Button size="lg" className="bg-white text-blue-700 hover:bg-slate-50 px-8 py-6 text-lg w-full sm:w-auto shadow-xl font-bold">
                 Criar Conta Agora
               </Button>
            </Link>
            <a href="https://wa.me/5524992818632" target="_blank" rel="noopener noreferrer">
               <Button size="lg" variant="outline" className="border-blue-400 text-white hover:bg-blue-600 px-8 py-6 text-lg w-full sm:w-auto font-bold group">
                 Falar com Consultor
                 <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
               </Button>
            </a>
          </div>
          <p className="mt-8 text-blue-200 text-sm font-medium">
            * Dispensa cartão de crédito nos 7 dias iniciais. Sem pegadinhas.
          </p>
        </div>
      </section>
    </div>
  );
};
